<script lang="ts">
  import { tick, onMount } from 'svelte';
  import BookOpen from '@lucide/svelte/icons/book-open';
  import Video from '@lucide/svelte/icons/video';
  import CalendarDays from '@lucide/svelte/icons/calendar-days';
  import ClipboardCheck from '@lucide/svelte/icons/clipboard-check';
  import MessageSquare from '@lucide/svelte/icons/message-square';
  import Globe from '@lucide/svelte/icons/globe';
  import Check from '@lucide/svelte/icons/check';
  import AppWorkspace from './app-workspace.svelte';
  import MobileApp from './mobile-app.svelte';
  import PhoneFrame from '$lib/components/site/phone-frame.svelte';

  let root = $state<HTMLElement>();
  let enhanced = $state(false);
  let descriptionPanel = $state<HTMLDivElement>();
  let active = $state('courses');
  let previous = $state<string | null>(null);
  let outgoing = $state<HTMLDivElement>();
  let panel = $state<HTMLDivElement>();
  let animatePanel: (() => void) | undefined;
  const features = [
    {
      id: 'courses',
      label: 'Courses',
      icon: BookOpen,
      title: 'A clear path through every subject.',
      description:
        'Bring video lessons, PDFs and exercises into a structured course. Organise the content once, then give each batch the access it needs.',
      details: ['Sections and lessons', 'Video and PDF materials', 'Saved learning progress'],
      screen: 'Course content'
    },
    {
      id: 'live',
      label: 'Live classes',
      icon: Video,
      title: 'Be there for the moment it clicks.',
      description:
        'Schedule a live class and give your students a clear place to join. Keep the session details and learning materials together in the course.',
      details: ['Scheduled live sessions', 'Classroom joining links', 'Calendar invitations'],
      screen: 'Live class'
    },
    {
      id: 'batches',
      label: 'Batches',
      icon: CalendarDays,
      title: 'The teaching day, in order.',
      description:
        'Assign teachers and enrol students in the right batch. Manage the timetable, take attendance and share notices with the people who need them.',
      details: [
        'Teachers and student enrollment',
        'Timetables and manual attendance',
        'Guardian links and batch notices'
      ],
      screen: 'Batch attendance'
    },
    {
      id: 'assessment',
      label: 'Assessments',
      icon: ClipboardCheck,
      title: 'See the thinking behind the answer.',
      description:
        'Set exercises, review submissions and give useful feedback. Keep marks connected to the student and the work that earned them.',
      details: ['Exercises and submissions', 'Grading and individual feedback', 'Course completion certificates'],
      screen: 'Submission review'
    },
    {
      id: 'community',
      label: 'Community',
      icon: MessageSquare,
      title: 'Keep the questions coming.',
      description:
        'Give your institution a shared space for questions and discussion. Students can learn from each other, and teachers can keep the conversation connected to the course.',
      details: ['Course-linked discussions', 'Replies and upvotes', 'Searchable questions'],
      screen: 'Institution community'
    },
    {
      id: 'website',
      label: 'Your website',
      icon: Globe,
      title: 'Your institution, unmistakably yours.',
      description:
        'Introduce your courses through a website with your institution’s identity. Choose a layout, add your branding and give learners one place to find you.',
      details: ['Institution branding', 'Course catalogue', 'Configurable page layouts'],
      screen: 'Institution website'
    }
  ];
  const feature = $derived(features.find((item) => item.id === active) ?? features[0]);

  async function selectFeature(featureId: string) {
    if (active === featureId) return;

    previous = active;
    active = featureId;
    await tick();
    if (active !== featureId) return;

    animatePanel?.();
  }

  onMount(() => {
    enhanced = true;
    let disposed = false;
    let frame = 0;
    let stopAnimation = () => {};
    const steps = Array.from(root?.querySelectorAll<HTMLElement>('[data-feature-step]') ?? []);
    function syncFeature() {
      frame = 0;
      if (!root) return;

      const bounds = root.getBoundingClientRect();
      if (bounds.top > innerHeight || bounds.bottom < 0) return;

      const mobileView = matchMedia('(max-width: 999px)').matches;
      const readingPosition = innerHeight * (mobileView ? 0.5 : 0.42);
      let closestStep = steps[0];
      let closestDistance = Infinity;
      for (const step of steps) {
        const stepBounds = step.getBoundingClientRect();
        const distance = Math.abs(stepBounds.top + stepBounds.height / 2 - readingPosition);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestStep = step;
        }
      }
      const featureId = closestStep?.dataset.featureStep;
      if (featureId) void selectFeature(featureId);
    }
    function requestSync() {
      if (frame) return;

      frame = requestAnimationFrame(syncFeature);
    }
    window.addEventListener('scroll', requestSync, { passive: true });
    window.addEventListener('resize', requestSync);
    const motionObserver = new MutationObserver(() => {
      if (document.documentElement.dataset.motion === 'paused') {
        stopAnimation();
        previous = null;
      }
    });
    motionObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['data-motion'] });
    import('motion')
      .then(({ animate }) => {
        if (disposed) return;

        let transitionVersion = 0;
        animatePanel = () => {
          stopAnimation();
          const version = ++transitionVersion;
          if (
            !panel ||
            matchMedia('(prefers-reduced-motion: reduce)').matches ||
            document.documentElement.dataset.motion === 'paused'
          ) {
            previous = null;
            return;
          }

          const entering = panel;
          const leaving = outgoing;
          const arrival = animate(
            entering,
            { opacity: [0, 1], y: [24, 0], scale: [0.985, 1], filter: ['blur(4px)', 'blur(0px)'] },
            { duration: 0.7, ease: [0.16, 1, 0.3, 1] }
          );
          const descriptionArrival = descriptionPanel
            ? animate(descriptionPanel, { opacity: [0.3, 1], y: [12, 0] }, { duration: 0.45, ease: [0.16, 1, 0.3, 1] })
            : undefined;
          const departure = leaving
            ? animate(
                leaving,
                { opacity: [1, 0], y: [0, -14], scale: [1, 0.995] },
                { duration: 0.5, ease: [0.16, 1, 0.3, 1] }
              )
            : undefined;
          stopAnimation = () => {
            transitionVersion += 1;
            arrival.stop();
            departure?.stop();
            descriptionArrival?.stop();
            for (const layer of [entering, leaving, descriptionPanel]) {
              layer?.style.removeProperty('transform');
              layer?.style.removeProperty('opacity');
              layer?.style.removeProperty('filter');
            }
          };
          Promise.resolve(arrival).then(() => {
            if (!disposed && version === transitionVersion) previous = null;
          });
        };
      })
      .catch(() => {
        previous = null;
      });
    requestSync();
    return () => {
      disposed = true;
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', requestSync);
      window.removeEventListener('resize', requestSync);
      motionObserver.disconnect();
      stopAnimation();
      animatePanel = undefined;
    };
  });
</script>

{#snippet screen(view: string)}
  <div class="workspace-view"><AppWorkspace {view} compact /></div>
  <div class="phone-view">
    <PhoneFrame label="Edvello {view} screen on a phone, with sample data"><MobileApp {view} /></PhoneFrame>
  </div>
{/snippet}

{#snippet description(item: (typeof features)[number])}
  <div class="feature-description">
    <span class="feature-label"><item.icon size={17} />{item.label}</span>
    <h3>{item.title}</h3>
    <p>{item.description}</p>
    <ul>
      {#each item.details as detail}<li><Check size={14} />{detail}</li>{/each}
    </ul>
  </div>
{/snippet}

<section id="platform" class="platform-showcase" data-enhanced={enhanced} bind:this={root}>
  <div class="showcase-inner">
    <div class="showcase-heading">
      <h2>The whole institution.<br />In one place.</h2>
      <p>From the first lesson to the final feedback.<br />Explore the tools behind your teaching day.</p>
    </div>
    <div class="feature-sequence">
      <div class="feature-steps">
        {#each features as item}<section
            class="feature-step"
            id={`platform-feature-${item.id}`}
            data-feature-step={item.id}
            aria-label={item.label}
          >
            {@render description(item)}
          </section>{/each}
      </div>
      <div class="desktop-showcase">
        <div class="mobile-feature-copy" bind:this={descriptionPanel}>
          {@render description(feature)}
        </div>
        <div class="showcase-entrance">
          <div class="showcase-screen">
            <div class="scene-viewport">
              {#if previous}<div class="scene-layer scene-outgoing" aria-hidden="true" bind:this={outgoing}>
                  {@render screen(previous)}
                </div>{/if}
              <div class="scene-layer scene-incoming" bind:this={panel}>{@render screen(active)}</div>
            </div>
            <div class="screen-caption"><span>{feature.screen}</span><span>Edvello workspace</span></div>
          </div>
        </div>
        <div class="sequence-progress" aria-hidden="true">
          {#each features as item}<span class:current={active === item.id}></span>{/each}
        </div>
      </div>
    </div>
  </div>
</section>

<style>
  .mobile-feature-copy,
  .phone-view {
    display: none;
  }
  .platform-showcase {
    background: #1447e6;
    color: #fff;
    padding: 100px 0 90px;
  }
  .showcase-inner {
    width: min(1240px, calc(100% - 80px));
    margin-inline: auto;
  }
  .showcase-heading {
    display: flex;
    align-items: end;
    justify-content: space-between;
    gap: 40px;
    margin-bottom: 70px;
  }
  h2 {
    font-size: clamp(40px, 5vw, 64px);
    font-weight: 500;
    line-height: 1.08;
    letter-spacing: -0.04em;
    margin: 0;
  }
  .showcase-heading > p {
    font-size: 14px;
    line-height: 1.7;
    color: #d8e3ff;
    padding-bottom: 6px;
  }
  .feature-sequence {
    display: grid;
    grid-template-columns: minmax(235px, 0.7fr) minmax(0, 1.8fr);
    align-items: start;
    gap: 45px;
  }
  .feature-step {
    min-height: 75vh;
    display: flex;
    align-items: center;
    scroll-margin-top: 135px;
  }
  .feature-step:last-child {
    min-height: 65vh;
  }
  .feature-description {
    padding-block: 35px;
  }
  .feature-label {
    display: inline-flex;
    align-items: center;
    gap: 9px;
    color: #f4edac;
    font-size: 12px;
    font-weight: 500;
    margin-bottom: 20px;
  }
  h3 {
    font-size: clamp(26px, 2.4vw, 35px);
    font-weight: 500;
    line-height: 1.16;
    letter-spacing: -0.03em;
    margin: 0 0 18px;
    text-wrap: balance;
  }
  .feature-description > p {
    font-size: 13px;
    line-height: 1.85;
    color: #d8e3ff;
    margin: 0;
  }
  ul {
    list-style: none;
    padding: 0;
    margin: 25px 0 0;
    display: grid;
    gap: 13px;
  }
  li {
    display: flex;
    align-items: start;
    gap: 8px;
    font-size: 11px;
    line-height: 1.6;
    color: #e1e9ff;
  }
  li :global(svg) {
    flex-shrink: 0;
    margin-top: 2px;
    color: #f4edac;
  }
  .desktop-showcase {
    position: sticky;
    top: 110px;
  }
  .showcase-entrance {
    perspective: 1400px;
  }
  .scene-viewport {
    position: relative;
    min-width: 0;
  }
  .scene-layer {
    transform-origin: center top;
  }
  .scene-outgoing {
    position: absolute;
    inset: 0;
    z-index: 1;
    pointer-events: none;
  }
  .scene-incoming {
    position: relative;
    z-index: 2;
  }
  .showcase-screen {
    overflow: hidden;
    border-radius: 12px;
    background: #fff;
    box-shadow: 0 24px 50px -25px #071c7180;
  }
  .screen-caption {
    display: flex;
    justify-content: space-between;
    gap: 20px;
    background: #e9effd;
    padding: 12px 18px;
    font-size: 9px;
    color: #536583;
  }
  .sequence-progress {
    display: flex;
    gap: 7px;
    width: 128px;
    margin: 20px auto 0;
  }
  .sequence-progress > span {
    flex: 1;
    height: 3px;
    background: #ffffff40;
    border-radius: 2px;
  }
  .sequence-progress > span.current {
    background: #f4edac;
  }
  @media (max-width: 1120px) and (min-width: 1000px) {
    .showcase-inner {
      width: calc(100% - 48px);
    }
    .feature-sequence {
      gap: 25px;
      grid-template-columns: minmax(220px, 0.7fr) minmax(0, 1.7fr);
    }
  }
  @media (max-width: 999px) {
    .platform-showcase {
      padding: 65px 0 45px;
    }
    .showcase-inner {
      width: min(700px, calc(100% - 36px));
    }
    .showcase-heading {
      display: block;
      margin-bottom: 28px;
    }
    h2 {
      font-size: clamp(38px, 7vw, 54px);
    }
    .showcase-heading > p {
      font-size: 13px;
      margin-top: 20px;
    }
    .feature-sequence {
      --mobile-stage-height: clamp(260px, 43svh, 360px);
      display: grid;
      grid-template-columns: 1fr;
      isolation: isolate;
    }
    .feature-steps {
      grid-column: 1;
      grid-row: 1;
      padding-top: calc(var(--mobile-stage-height) + 45px);
      min-width: 0;
    }
    .desktop-showcase {
      display: block;
      min-width: 0;
      grid-column: 1;
      grid-row: 1;
      position: sticky;
      top: 87px;
      z-index: 4;
      margin-inline: -2px;
      padding-bottom: 18px;
      border-top: 12px solid #1447e6;
      margin-top: -12px;
      background: linear-gradient(#1447e6 90%, #1447e600);
    }
    .scene-viewport {
      height: var(--mobile-stage-height);
    }
    .scene-layer {
      height: 100%;
    }
    .workspace-view {
      display: none;
    }
    .phone-view {
      display: block;
      height: 100%;
      aspect-ratio: 428 / 868;
      max-width: 100%;
      margin-inline: auto;
    }
    .showcase-screen {
      overflow: visible;
      border-radius: 0;
      background: none;
      box-shadow: none;
    }
    .screen-caption {
      display: none !important;
    }
    .sequence-progress {
      margin-top: 8px;
      visibility: hidden;
    }
    .screen-caption {
      padding: 11px 15px;
      font-size: 10px;
    }
    .feature-step,
    .feature-step:last-child {
      min-height: 70svh;
      display: flex;
      align-items: center;
      padding-block: 40px 100px;
      scroll-margin-top: 95px;
    }
    .feature-step + .feature-step {
      border-top: 0;
    }
    .feature-description {
      padding-block: 25px;
    }
    .feature-label {
      margin-top: 24px;
      margin-bottom: 14px;
    }
    h3 {
      font-size: 30px;
      max-width: 480px;
    }
    .feature-description > p {
      font-size: 15px;
      line-height: 1.75;
      max-width: 600px;
    }
    li {
      font-size: 13px;
    }
    ul {
      gap: 9px;
      margin-top: 18px;
    }
    .platform-showcase[data-enhanced='true'] .feature-steps {
      padding-top: 0;
    }
    .platform-showcase[data-enhanced='true'] .feature-step,
    .platform-showcase[data-enhanced='true'] .feature-step:last-child {
      min-height: 90svh;
      padding: 0;
    }
    .platform-showcase[data-enhanced='true'] .feature-step > .feature-description {
      display: none;
    }
    .platform-showcase[data-enhanced='true'] .desktop-showcase {
      height: calc(100svh - 112px);
      top: 88px;
      display: flex;
      flex-direction: column;
      gap: 16px;
      padding: 0;
      border: 0;
      margin: 0;
      background: #1447e6;
    }
    .platform-showcase[data-enhanced='true'] .mobile-feature-copy {
      display: block;
      flex-shrink: 0;
      text-align: left;
    }
    .mobile-feature-copy .feature-description {
      padding: 8px 0 0;
    }
    .mobile-feature-copy .feature-label {
      margin: 0 0 10px;
      font-size: 11px;
    }
    .mobile-feature-copy h3 {
      font-size: clamp(24px, 5.5vw, 30px);
      max-width: 520px;
      margin-bottom: 10px;
    }
    .mobile-feature-copy .feature-description > p {
      font-size: 13px;
      line-height: 1.65;
    }
    .mobile-feature-copy ul {
      display: none;
    }
    .platform-showcase[data-enhanced='true'] .showcase-entrance {
      flex: 1;
      min-height: 0;
      display: flex;
      justify-content: center;
    }
    .platform-showcase[data-enhanced='true'] .showcase-screen {
      width: 100%;
      height: 100%;
    }
    .platform-showcase[data-enhanced='true'] .scene-viewport {
      height: 100%;
    }
    .platform-showcase[data-enhanced='true'] .sequence-progress {
      flex-shrink: 0;
      visibility: visible;
      margin: 0 auto;
    }
  }
</style>
